import React, { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button, Space, message } from 'antd';
import { useIntl } from 'react-intl';
import { sanitizeTrustedHtml } from '../../../utils/sanitize';
import { useUploadCrmEmailImageMutation } from '../../../services/nestjs-base-api';
import { useAuth } from '../../../hooks/useAuth';

// Render email bodies with the same tight paragraph spacing the sent email uses
// (ses.service wraps each <p> at margin: 0 0 12px). Without this the preview and
// timeline use the browser's default ~1em <p> margins, which — combined with any
// blank paragraphs — reads as double-spacing that the real email won't have.
const EMAIL_BODY_STYLE_ID = 'crm-email-body-style';
if (typeof document !== 'undefined') {
    // Overwrite (not skip) an existing block so hot reload picks up rule
    // changes — a create-once guard serves stale CSS for the whole session.
    let styleEl = document.getElementById(EMAIL_BODY_STYLE_ID) as HTMLStyleElement | null;
    if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = EMAIL_BODY_STYLE_ID;
        document.head.appendChild(styleEl);
    }
    styleEl.textContent =
        '.crm-email-body p{margin:0 0 12px 0;line-height:1.5}' +
        '.crm-email-body p:last-child{margin-bottom:0}' +
        '.crm-email-body ul,.crm-email-body ol{margin:0 0 12px 0}' +
        // Size the EDITOR AREA and let the outer .quill auto-grow around the
        // toolbar. A fixed height on <ReactQuill> itself makes the content
        // overflow by the toolbar's height (worse when it wraps to two rows),
        // painting over whatever is rendered below the editor.
        '.crm-quill-wrapper .ql-container{min-height:var(--crm-quill-min-height,200px)}' +
        // Quill's stylesheet zeroes paragraph margins, so a multi-paragraph
        // body reads as one cramped block while editing and then arrives
        // properly spaced. Match the sent email here too — that parity is the
        // whole point of this style block.
        '.crm-quill-wrapper .ql-editor p{margin:0 0 12px 0;line-height:1.5}' +
        '.crm-quill-wrapper .ql-editor p:last-child{margin-bottom:0}' +
        // Pasted inline images must never overflow the editor or the timeline.
        '.crm-quill-wrapper .ql-editor img{max-width:100%}' +
        '.crm-email-body img{max-width:100%}';
}

// Shared WYSIWYG config for CRM email composing and templates. Mirrors the
// admin-broadcast editor (AdminMessagesPage) so the two feel identical and a
// template authored in one place renders the same in the other.
export const crmQuillModules = {
    toolbar: [
        [{ header: [2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['link'],
        [{ align: [] }],
        ['clean'],
    ],
};

export const crmQuillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'indent',
    'link', 'align', 'image',
    // Quoted history in a Platform Mail reply. Without blockquote whitelisted
    // Quill strips the wrapper and the quote reads as if the sender wrote it.
    'blockquote',
    // Attribute formats used by the image size menu — without these in the
    // whitelist, formatText silently drops the width/height attributes.
    'width', 'height',
];

/**
 * True when the editor holds no visible content. ReactQuill emits
 * `<p><br></p>` for an empty editor — a non-empty string that is visually
 * blank — so a naive Antd `required` rule passes on empty input without this.
 */
export const isQuillEmpty = (html?: string | null): boolean => {
    if (!html) return true;
    return html.replace(/<(p|br|div|span)[^>]*>/gi, '')
        .replace(/<\/(p|div|span)>/gi, '')
        .replace(/&nbsp;/gi, '')
        .replace(/\s/g, '')
        .length === 0;
};

/**
 * Renders a stored email body. New bodies are HTML (sanitised on render, the
 * CRM's trust boundary — the API stores admin-authored HTML unsanitised).
 * Legacy rows hold plain text with newlines; detect the absence of any tag and
 * fall back to a pre-wrap block so their line breaks survive.
 */
export const EmailBodyHtml: React.FC<{
    body?: string | null;
    style?: React.CSSProperties;
}> = ({ body, style }) => {
    const value = body || '';
    const looksLikeHtml = /<[a-z][\s\S]*>/i.test(value);
    if (!looksLikeHtml) {
        return <div style={{ whiteSpace: 'pre-wrap', ...style }}>{value}</div>;
    }
    // Drop blank paragraphs so the preview matches the sent email — the API's
    // prepareHtmlForEmail strips these too, so leaving them here would make the
    // preview look double-spaced against an email that isn't.
    const cleaned = value
        .replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, '')
        .replace(/<p>\s*<\/p>/gi, '');
    return (
        <div
            className="crm-email-body"
            style={style}
            dangerouslySetInnerHTML={{ __html: sanitizeTrustedHtml(cleaned) }}
        />
    );
};

// Antd Form-friendly Quill field (value / onChange contract).
export const CrmRichTextEditor: React.FC<{
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    minHeight?: number;
}> = ({ value, onChange, placeholder, minHeight = 200 }) => {
    const quillRef = useRef<ReactQuill>(null);
    const intl = useIntl();
    const { user } = useAuth();
    const [uploadEmailImage] = useUploadCrmEmailImageMutation();
    // Click-to-size menu for embedded images: position within the wrapper
    // plus the clicked <img>.
    const [imageMenu, setImageMenu] = useState<{
        top: number;
        left: number;
        img: HTMLImageElement;
    } | null>(null);
    // Ref-bridged so the mount-once paste/drop listeners never hold a stale
    // token, mutation trigger or locale.
    const uploadRef = useRef<(file: File) => Promise<string>>();
    uploadRef.current = async (file: File) => {
        const { url } = await uploadEmailImage({
            jwtToken: user?.jwtToken || '',
            file,
        }).unwrap();
        return url;
    };
    const intlRef = useRef(intl);
    intlRef.current = intl;

    // Write the width through Quill's delta (the image blot whitelists the
    // width ATTRIBUTE) — mutating the DOM directly would leave the form value
    // stale, and attributes survive both Quill's clipboard convert and the
    // sanitiser, and are honoured by email clients.
    const applyImageSize = (width: string | false) => {
        const editor = quillRef.current?.getEditor();
        const img = imageMenu?.img;
        if (!editor || !img) return;
        const QuillCtor = (ReactQuill as any).Quill;
        const blot = QuillCtor.find(img);
        if (blot) {
            const index = editor.getIndex(blot);
            editor.formatText(index, 1, { width }, 'user');
        }
        setImageMenu(null);
    };
    // The last HTML we know is in the editor — set either by our own imperative
    // load or echoed back from the user's typing. Lets the value effect below
    // distinguish an external value change (a template load) from an echo of the
    // edit the user just made, so we never re-apply and jump the cursor.
    const lastKnownRef = useRef<string>('');

    useEffect(() => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;

        // Strip highlight (background colour) from pasted content. Source docs
        // and emails often highlight merge fields like {firstName}; that
        // background style would otherwise survive the paste and, after
        // substitution, leave the recipient's name highlighted in the send.
        editor.clipboard.addMatcher(Node.ELEMENT_NODE, (_node, delta) => {
            const ops = delta.ops as
                | Array<{ attributes?: Record<string, unknown> }>
                | undefined;
            ops?.forEach((op) => {
                if (op.attributes && 'background' in op.attributes) {
                    delete op.attributes.background;
                }
            });
            return delta;
        });

        // Rescue paragraph structure on paste. Some sources express line breaks
        // as whitespace between inline elements rather than block/<br> tags;
        // HTML collapses that whitespace, so Quill's converter flattens the whole
        // message into one line — even when it keeps the inline bold/underline.
        // We can't tell reliably from the markup, so we take over the paste,
        // convert the HTML, and compare its line count to the plain-text
        // alternative. If the HTML preserved the structure we use it (formatting
        // intact); if it flattened, we rebuild paragraphs from the plain text.
        const root = editor.root;
        const countNewlines = (delta: { ops?: Array<{ insert?: unknown }> }) =>
            (delta.ops || []).reduce(
                (n, op) =>
                    n +
                    (typeof op.insert === 'string'
                        ? (op.insert.match(/\n/g) || []).length
                        : 0),
                0,
            );
        // Upload image files and embed them at the cursor as permanent public
        // URLs. Quill's default would inline pasted images as data: URIs,
        // which Gmail and most email clients strip — so uploads are the only
        // way an image survives to the recipient.
        const insertImages = async (files: File[]) => {
            for (const file of files) {
                const hide = message.loading(
                    intlRef.current.formatMessage({ id: 'crm.email.imageUploading' }),
                    0,
                );
                try {
                    const url = await uploadRef.current!(file);
                    const range = editor.getSelection(true);
                    const index = range ? range.index : editor.getLength();
                    editor.insertEmbed(index, 'image', url, 'user');
                    editor.setSelection(index + 1, 0, 'silent' as never);
                } catch (err: any) {
                    message.error(
                        err?.data?.message ||
                            intlRef.current.formatMessage({ id: 'crm.email.imageUploadFailed' }),
                    );
                } finally {
                    hide();
                }
            }
        };
        const imageFilesOf = (list: FileList | undefined | null): File[] =>
            Array.from(list || []).filter((f) => f.type.startsWith('image/'));

        const onDrop = (e: DragEvent) => {
            const images = imageFilesOf(e.dataTransfer?.files);
            if (images.length === 0) return;
            e.preventDefault();
            e.stopPropagation();
            void insertImages(images);
        };

        const onPaste = (e: ClipboardEvent) => {
            const cd = e.clipboardData;
            if (!cd) return;
            const pastedImages = imageFilesOf(cd.files);
            if (pastedImages.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                void insertImages(pastedImages);
                return;
            }
            const plain = (cd.getData('text/plain') || '').replace(/\r\n/g, '\n');
            // Single-line / simple pastes: let Quill handle them normally.
            if (!/\n/.test(plain)) return;
            const html = cd.getData('text/html');

            e.preventDefault();
            e.stopPropagation();
            const range = editor.getSelection(true);
            const index = range ? range.index : editor.getLength();
            if (range && range.length) {
                editor.deleteText(range.index, range.length, 'user');
            }

            // Runtime is the nested Quill 1.3.7 whose convert() takes an HTML
            // string; the resolved types are from hoisted quill@2 (which expects
            // an object), so bridge the mismatch with a cast.
            const convert = editor.clipboard.convert.bind(editor.clipboard) as unknown as (
                h: string,
            ) => { ops?: Array<{ insert?: unknown }> };
            const htmlNewlines = html ? countNewlines(convert(html)) : 0;
            // Non-blank lines the plain text implies. If the HTML kept roughly
            // that many breaks, trust it (keeps formatting); otherwise it
            // flattened, so rebuild from the plain text.
            const plainLines = plain.split('\n').filter((l) => l.trim()).length;
            if (html && htmlNewlines >= plainLines - 1) {
                editor.clipboard.dangerouslyPasteHTML(index, html, 'user');
            } else {
                const esc = (s: string) =>
                    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                const htmlOut = plain
                    .split(/\n{2,}/)
                    .map((para) => `<p>${esc(para).replace(/\n/g, '<br>')}</p>`)
                    .join('');
                editor.clipboard.dangerouslyPasteHTML(index, htmlOut, 'user');
            }
        };
        // Click an embedded image → size menu; click anywhere else hides it.
        const wrapper = root.closest('.crm-quill-wrapper') as HTMLElement | null;
        const onClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target?.tagName === 'IMG' && wrapper) {
                const imgRect = target.getBoundingClientRect();
                const wrapRect = wrapper.getBoundingClientRect();
                setImageMenu({
                    top: imgRect.top - wrapRect.top - 34,
                    left: Math.max(0, imgRect.left - wrapRect.left),
                    img: target as HTMLImageElement,
                });
            } else {
                setImageMenu(null);
            }
        };
        root.addEventListener('click', onClick);

        root.addEventListener('paste', onPaste, true); // capture: runs before Quill
        root.addEventListener('drop', onDrop, true);
        return () => {
            root.removeEventListener('click', onClick);
            root.removeEventListener('paste', onPaste, true);
            root.removeEventListener('drop', onDrop, true);
        };
    }, []);

    // Apply external value changes (e.g. loading a template) imperatively AFTER
    // mount. react-quill flattens multi-paragraph HTML into a single block when
    // the value is present at mount (a Quill 1.3.7 clipboard quirk with a
    // freshly-created editor) but applies the exact same value correctly once
    // mounted — so we render it uncontrolled (empty at mount) and push the value
    // in here. Uses the 'silent' source so this load does not fire onChange and
    // clobber the form value the caller already set.
    useEffect(() => {
        const editor = quillRef.current?.getEditor();
        if (!editor) return;
        const incoming = value || '';
        if (incoming === lastKnownRef.current) return; // our own echo — ignore
        if (incoming === editor.root.innerHTML) {
            lastKnownRef.current = incoming;
            return;
        }
        lastKnownRef.current = incoming;
        if (!incoming) {
            editor.setContents([] as never, 'silent' as never);
            return;
        }
        const convert = editor.clipboard.convert.bind(editor.clipboard) as unknown as (
            h: string,
        ) => never;
        editor.setContents(convert(incoming), 'silent' as never);
    }, [value]);

    const handleChange = (html: string) => {
        lastKnownRef.current = html;
        onChange?.(html);
    };

    return (
        <div
            className="crm-quill-wrapper"
            style={
                {
                    position: 'relative',
                    ['--crm-quill-min-height' as string]: `${minHeight}px`,
                } as React.CSSProperties
            }
        >
            <ReactQuill
                ref={quillRef}
                theme="snow"
                defaultValue=""
                onChange={handleChange}
                modules={crmQuillModules}
                formats={crmQuillFormats}
                placeholder={placeholder}
            />
            {imageMenu && (
                <div
                    style={{
                        position: 'absolute',
                        top: imageMenu.top,
                        left: imageMenu.left,
                        zIndex: 10,
                        background: '#fff',
                        border: '1px solid #d9d9d9',
                        borderRadius: 6,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        padding: 2,
                    }}
                >
                    <Space size={0}>
                        <Button type="text" size="small" onClick={() => applyImageSize('240')}>
                            {intl.formatMessage({ id: 'crm.email.imageSize.small' })}
                        </Button>
                        <Button type="text" size="small" onClick={() => applyImageSize('480')}>
                            {intl.formatMessage({ id: 'crm.email.imageSize.medium' })}
                        </Button>
                        <Button type="text" size="small" onClick={() => applyImageSize('720')}>
                            {intl.formatMessage({ id: 'crm.email.imageSize.large' })}
                        </Button>
                        <Button type="text" size="small" onClick={() => applyImageSize(false)}>
                            {intl.formatMessage({ id: 'crm.email.imageSize.original' })}
                        </Button>
                    </Space>
                </div>
            )}
        </div>
    );
};
